<template>
  <div class="mb-4 flex justify-end py-2">
    <LinkToCreate :to="{ name: 'productCreate' }">
      Add Product
    </LinkToCreate>
  </div>
  <BaseCard
    class="flex size-full grow flex-col overflow-hidden"
  >
    <QueryLoading v-if="isLoading" class="flex-1" />
    <QueryError
      v-else-if="isError"
      :show-retry="true"
      class="m-auto"
      @retry="refetch"
    >
      Failed to load products
    </QueryError>
    <template v-else>
      <div class="flex justify-between">
        <SearchInput
          v-model="filters.search"
          class="mb-4 w-full max-w-md shrink-0"
        />
      </div>
      <div class="h-full overflow-y-auto">
        <BaseTable :rows="products" :columns="columns">
          <template #cell-color="{ value }">
            <ColorItem :value="value" rounded />
          </template>

          <template #cell-name="{ row }">
            <div class="w-full">
              <p
                class="mr-2 line-clamp-1 text-sm text-background-fg/50"
              >
                {{ row.categoryPath }}
              </p>
              <p class="font-semibold">
                {{ row.name }}
              </p>
            </div>
          </template>

          <template #cell-tags="{ value }">
            <ul class="flex flex-wrap justify-end gap-1">
              <template v-for="tagId in value" :key="tagId">
                <template v-if="tagId">
                  <li>
                    <ProductTagChip
                      :product-tag-id="tagId"
                    />
                  </li>
                </template>
              </template>
            </ul>
          </template>

          <template #cell-actions="{ row }">
            <div
              class="flex items-center justify-end space-x-2"
            >
              <BaseTableButton
                variant="info"
                :to="{
                  name: 'productEdit',
                  params: {
                    productId: row.id,
                  },
                }"
              >
                Edit
              </BaseTableButton>

              <BaseTableButton
                variant="danger"
                @click="onDeleteProduct(row.id)"
              >
                Delete
              </BaseTableButton>
            </div>
          </template>

          <template #grid-card="{ row }">
            <div
              class="flex w-full items-center space-x-2 border-l-2 pl-2"
              :style="{ borderColor: row.color }"
            >
              <div class="flex-1">
                <p
                  class="mr-2 line-clamp-1 text-sm text-background-fg/50"
                >
                  {{ row.categoryPath }}
                </p>
                <p class="font-semibold">
                  {{ row.name }}
                </p>
              </div>
              <div>
                <p class="font-medium">
                  {{ getProductPriceString(row) }}
                </p>
              </div>
              <ButtonWithIcon
                @click="onClickThreeDots(row.id)"
              >
                <IconVerticalEllipsis />
              </ButtonWithIcon>
            </div>
          </template>
        </BaseTable>
      </div>
    </template>
  </BaseCard>

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    title="Are you sure you wish to delete this product?"
    confirm-label="Delete Product"
    cancel-label="No"
  />

  <BaseDialog
    :is-open="isActionsDialogOpen"
    @close="onActionsDialogCancel"
  >
    <ul class="flex flex-col space-y-2 p-2">
      <li class="relative cursor-default select-none">
        <button
          :class="[
            'block w-full truncate py-2 capitalize',
            'hover:bg-background-fg/3',
          ]"
          type="button"
          @click="onActionsDialogConfirm('edit')"
        >
          Edit
        </button>
      </li>

      <li class="relative cursor-default select-none">
        <button
          :class="[
            'block w-full truncate py-2 capitalize',
            'hover:bg-background-fg/3',
          ]"
          type="button"
          @click="onActionsDialogConfirm('delete')"
        >
          Delete
        </button>
      </li>
    </ul>
  </BaseDialog>
</template>

<script setup lang="ts">
import type { TableColumn } from "@/components/BaseTable.vue"
import type { ListProductsResponseItem } from "@/services/sharedTypes/products"

const { isVatEnabled } = useTenant()
const { formatToMerchantCurrencyString } = useCurrency()
const filters = reactive({
  search: "",
})
const {
  data: products,
  isError,
  refetch,
  isLoading,
} = useProducts(filters)

type ColumnId =
  | "color"
  | "name"
  | "tags"
  | "price"
  | "vat"
  | "actions"
const columns = ref<
  Array<TableColumn<ListProductsResponseItem, ColumnId>>
>([
  {
    id: "color",
    header: "",
    getCellValue: (row) => row.color,
    autoWidth: true,
  },
  {
    id: "name",
    header: "Name",
    getCellValue: (row) => row.name,
  },
  {
    id: "tags",
    header: "Tags",
    getCellValue: (row) => row.tagIds,
    // autoWidth: true,
    align: "right",
  },
  {
    id: "price",
    header: "Price",
    getCellValue: (row) => getProductPriceString(row),
    autoWidth: true,
  },

  ...(isVatEnabled.value
    ? [
        {
          id: "vat",
          header: "VAT Rate",
          getCellValue: (row: ListProductsResponseItem) =>
            row.vatRate
              ? `${formatToLocalizedNumber(row.vatRate)} %`
              : "",
          autoWidth: true,
        } as const,
      ]
    : []),
  {
    id: "actions",
    autoWidth: true,
  },
])

const deleteConfirmDialogRef = useTemplateRef(
  "deleteConfirmDialogRef"
)
const onDeleteProduct = async (productId: string) => {
  if (deleteConfirmDialogRef.value === null) return
  const { isCanceled: isDeleteCancelled } =
    await deleteConfirmDialogRef.value.reveal()
  if (isDeleteCancelled) return

  try {
    await deleteProduct(productId)
    toast.success("Product deleted")
  } catch (error) {
    toast.error("Failed to delete product")
    logError(error)
  }
}

const getProductPriceString = (
  product: ListProductsResponseItem
) =>
  product.hasVariablePrice
    ? "variable"
    : formatToMerchantCurrencyString(product.price)

const {
  reveal: revealActionsDialog,
  isRevealed: isActionsDialogOpen,
  cancel: onActionsDialogCancel,
  confirm: onActionsDialogConfirm,
} = useConfirmDialog<undefined, "edit" | "delete">()

const router = useRouter()
const onClickThreeDots = async (productId: string) => {
  const { data, isCanceled } = await revealActionsDialog()
  if (isCanceled) return
  if (data === "edit") {
    router.push({
      name: "productEdit",
      params: {
        productId: productId,
      },
    })
  } else if (data === "delete") {
    onDeleteProduct(productId)
  } else {
    return
  }
}
</script>
